/**
 * Home wallet cards. Tap: Add expense for that house. Hold and drag (mouse: press and
 * move; touch: short press then drag): reorder, saved per user. Keyboard: Alt + arrows.
 */
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

import type { Entity, Wallet } from '../api/types.ts';
import { useT } from '../i18n/index.ts';
import { moveTo } from '../lib/entityOrder.ts';
import { rs } from '../lib/money.ts';
import { navigate } from '../lib/router.ts';
import { useHoldDrag } from '../lib/useHoldDrag.ts';
import { EntityIcon, Icon } from '../ui/icons.tsx';
import { Num, Spinner } from '../ui/parts.tsx';
import { mayGiveCash } from '../admin/perms.ts';
import { useSpender } from './store.tsx';

export function WalletGrid() {
  const { t } = useT();
  const s = useSpender();
  const ids = s.entities.map((e) => e.id);
  const dnd = useHoldDrag({ ids, onCommit: s.setEntityOrder });
  const byId = new Map(s.entities.map((e) => [e.id, e]));
  const dragged = dnd.drag ? byId.get(dnd.drag.id) : undefined;
  // The card tapped: Owners and Super Admins are asked what to do with that wallet (add an
  // expense, or give cash from it); an office boy goes straight to Add expense, as before.
  const [choiceFor, setChoiceFor] = useState<string | null>(null);

  function keyMove(e: ReactKeyboardEvent, id: string) {
    if (!e.altKey) return;
    const back = e.key === 'ArrowUp' || e.key === 'ArrowLeft';
    const fwd = e.key === 'ArrowDown' || e.key === 'ArrowRight';
    if (!back && !fwd) return;
    e.preventDefault();
    const i = ids.indexOf(id);
    s.setEntityOrder(moveTo(ids, id, back ? i - 1 : i + 1));
  }

  return (
    <>
      <span id="wallet-move-hint" className="sr-only">
        {t('home.holdToMove')}
      </span>
      {/* One card per row on phones, two on tablets, all four in a row on laptops. */}
      <div className="grid gap-3 low:gap-2 short:grid-cols-4 short:gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {dnd.order.map((id) => {
          const e = byId.get(id);
          if (!e) return null;
          // The lifted card's own element stays in the page (touch events stay tied to it)
          // and shows as the empty slot it will drop into.
          const slot = dnd.isSlot(id);
          return (
            <button
              key={id}
              type="button"
              {...dnd.bind(id)}
              onClick={() => (mayGiveCash(s.me, id) ? setChoiceFor(id) : navigate(`/add?entity=${id}`))}
              onKeyDown={(ev) => keyMove(ev, id)}
              aria-label={t('home.addFor', { entity: e.name })}
              aria-describedby="wallet-move-hint"
              className={`flex min-h-[84px] touch-manipulation items-center gap-3 rounded-[14px] py-2.5 ps-4 pe-3 text-start text-white transition-[transform,filter] duration-150 select-none [-webkit-touch-callout:none] low:min-h-[68px] tight:min-h-[60px] short:min-h-[60px] sm:min-h-24 short:sm:min-h-[60px] ${
                slot
                  ? 'border-2 border-dashed border-line-strong'
                  : dnd.pressed === id
                    ? 'scale-[0.97] brightness-110'
                    : 'hover:brightness-110 active:scale-[0.98]'
              }`}
              style={{ background: slot ? 'rgba(255,255,255,0.6)' : e.colour }}
            >
              <span className={`flex min-w-0 flex-1 items-center gap-3 ${slot ? 'invisible' : ''}`}>
                <CardBody entity={e} wallet={s.wallets.find((w) => w.entityId === id)} loading={s.loading} />
              </span>
            </button>
          );
        })}
      </div>

      {dnd.drag && dragged && (
        // The lifted card following the pointer.
        <div
          aria-hidden="true"
          className="pointer-events-none fixed z-50 flex items-center gap-3 rounded-[14px] py-2.5 ps-4 pe-3 text-start text-white shadow-[0_18px_40px_rgba(19,33,31,0.35)] ring-4 ring-white"
          style={{
            background: dragged.colour,
            width: dnd.drag.w,
            height: dnd.drag.h,
            left: dnd.drag.x - dnd.drag.offX,
            top: dnd.drag.y - dnd.drag.offY,
            transform: 'scale(1.04) rotate(-1deg)',
          }}
        >
          <CardBody entity={dragged} wallet={s.wallets.find((w) => w.entityId === dragged.id)} loading={false} />
        </div>
      )}

      {dnd.drag && (
        <p role="status" className="m-0 flex items-center gap-2 text-sm font-bold text-ink">
          <Icon name="arrange" size={16} />
          {t('home.dropHint')}
        </p>
      )}
      <WalletChoice entity={choiceFor ? byId.get(choiceFor) : undefined} onClose={() => setChoiceFor(null)} />
    </>
  );
}

function CardBody({ entity: e, wallet: w, loading }: { entity: Entity; wallet: Wallet | undefined; loading: boolean }) {
  const { t } = useT();
  const balance = w?.balance ?? 0;
  const low = w ? balance < w.lowBalanceThreshold : false;
  return (
    <>
      <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-white/20 max-[359px]:hidden">
        <EntityIcon entity={e} size={24} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
        <span className="text-[17px] leading-snug font-bold rtl:text-[19px]">{e.name}</span>
        {/* Only the portfolio's name and its owner's name (owner's request, 12 Oct 2026). */}
        {e.ownerName && <span className="text-[13px] leading-snug font-semibold text-white/90">{e.ownerName}</span>}
        {low && (
          <span className="inline-flex h-[26px] items-center gap-1 rounded-md bg-paper px-2 text-[13px] font-bold whitespace-nowrap text-danger">
            <Icon name="alert" size={14} stroke={2.5} />
            {t('home.low')}
          </span>
        )}
      </span>
      <Num className="text-[clamp(20px,6.5vw,26px)] font-bold tracking-tight whitespace-nowrap sm:text-[26px]">
        {loading ? <Spinner size={22} /> : rs(balance)}
      </Num>
    </>
  );
}

/** Tapped a wallet card: add an expense for that place, or give cash from that wallet. */
function WalletChoice({ entity, onClose }: { entity: Entity | undefined; onClose: () => void }) {
  const { t } = useT();
  const s = useSpender();
  const w = entity ? s.wallets.find((x) => x.entityId === entity.id && (!x.userId || x.userId === s.me.id)) : undefined;
  const inHand = w ? w.balance - w.unacknowledgedCashIn : 0;
  const go = (path: string) => {
    onClose();
    navigate(path);
  };
  const option =
    'flex min-h-[72px] w-full items-center gap-3.5 rounded-[14px] border-[1.5px] px-4 py-3 text-start focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-ink';
  return (
    <Dialog open={entity !== undefined} onClose={onClose} className="relative z-50">
      <DialogBackdrop transition className="fixed inset-0 bg-ink/45 transition duration-200 data-closed:opacity-0" />
      {/* A sheet from the bottom on phones, a small card in the middle on wider screens. */}
      <div className="fixed inset-0 flex items-end justify-center sm:items-center sm:p-6">
        <DialogPanel
          transition
          className="safe-bottom w-full max-w-md rounded-t-2xl bg-paper p-4 pb-5 shadow-[0_-12px_40px_rgba(19,33,31,0.25)] transition duration-200 data-closed:translate-y-6 data-closed:opacity-0 sm:rounded-2xl"
        >
          {entity && (
            <>
              <div className="mb-3.5 flex items-center gap-3">
                <span className="flex size-11 shrink-0 items-center justify-center rounded-xl text-white" style={{ background: entity.colour }}>
                  <EntityIcon entity={entity} size={22} />
                </span>
                <span className="flex min-w-0 flex-1 flex-col leading-tight">
                  <DialogTitle className="m-0 text-[19px] font-bold rtl:text-[21px]">{t('give.chooseTitle')}</DialogTitle>
                  <Num className="truncate text-[14px] text-muted">{t('give.inHandOf', { entity: entity.name, amount: rs(inHand) })}</Num>
                </span>
              </div>
              <div className="flex flex-col gap-2.5">
                <button type="button" onClick={() => go(`/add?entity=${entity.id}`)} className={`${option} border-brand bg-brand text-white hover:bg-brand-dark`}>
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-white/15">
                    <Icon name="plus" size={22} stroke={2.75} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col leading-snug">
                    <span className="text-[17px] font-bold rtl:text-[19px]">{t('give.chooseAdd')}</span>
                    <span className="text-sm text-white/85">{t('give.chooseAddSub')}</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => go(`/give?entity=${entity.id}`)}
                  className={`${option} border-line-strong bg-paper hover:bg-ground`}
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-tint text-brand-dark">
                    <Icon name="wallet" size={20} />
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col leading-snug">
                    <span className="text-[17px] font-bold rtl:text-[19px]">{t('give.chooseGive')}</span>
                    <span className="text-sm text-muted">{t('give.chooseGiveSub')}</span>
                  </span>
                </button>
                <button type="button" onClick={onClose} className="mt-1 h-12 rounded-xl text-[15px] font-semibold text-muted hover:bg-ground">
                  {t('app.cancel')}
                </button>
              </div>
            </>
          )}
        </DialogPanel>
      </div>
    </Dialog>
  );
}
